import { type JSX, type ParentComponent, ErrorBoundary as SolidErrorBoundary } from "solid-js";

interface ErrorBoundaryProps {
  fallback?: (error: Error, reset: () => void) => JSX.Element;
}

const DefaultErrorFallback = (error: Error, reset: () => void) => (
  <div class="p-6 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-950/20">
    <h3 class="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">Something went wrong</h3>
    <details class="mb-4">
      <summary class="cursor-pointer text-red-700 dark:text-red-300 mb-2">Error details</summary>
      <pre class="text-sm text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20 p-3 rounded overflow-auto">
        {error.name}: {error.message}
        {error.stack && (
          <>
            {"\n\n"}
            {error.stack}
          </>
        )}
      </pre>
    </details>
    <button
      onClick={reset}
      class="px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
    >
      Try again
    </button>
  </div>
);

export const ErrorBoundary: ParentComponent<ErrorBoundaryProps> = (props) => {
  return (
    <SolidErrorBoundary
      fallback={(error: Error, reset: () => void) => {
        console.error("ErrorBoundary caught error:", error);

        if (props.fallback) {
          return props.fallback(error, reset);
        }

        return DefaultErrorFallback(error, reset);
      }}
    >
      {props.children}
    </SolidErrorBoundary>
  );
};
